/**
 * ErdProfile — Entity-Relationship Diagrams (reference implementation).
 *
 * The software-design vertical's reference profile. When adding other
 * software-design profiles (database, class, sequence, component,
 * usecase, state, activity, dfd, c4, adr) follow this file's structure:
 * all required DomainDataProfile fields, a compact fieldAliases table,
 * a propsToAttrsMapping table with at least the identity fields, and
 * a `domainId` matching the RDM `@type` in the example file.
 *
 * See plans/software-design/domain.md § 1 for the ERD grammar, supported
 * notations (Crow's Foot, Chen's, UML-style), and relationship subtypes.
 */

import type { DomainDataProfile } from '../../../core/data-profiles';
import type { DiagramType } from '../../../lib/diagram/registries';

export type ErdNodeType = 'erd_entity' | 'erd_column' | 'erd_schema';
export type ErdEdgeType = 'fk' | 'one_to_one' | 'one_to_many' | 'many_to_many';

export const ErdProfile: DomainDataProfile<ErdNodeType, ErdEdgeType> = {
  domainId: 'erd',
  profileVersion: '1.0',
  // Reuses the existing DiagramType 'database' slot — the concrete RDM
  // @type string is 'erd', which the parser dispatches on. Epic B may
  // extend DiagramType directly; for the scaffold we reuse.
  diagramTypes: ['database' as DiagramType],

  nodeTypes: ['erd_entity', 'erd_column', 'erd_schema'],
  edgeTypes: ['fk', 'one_to_one', 'one_to_many', 'many_to_many'],

  vocabulary: {
    recordSingular: 'Entity',
    recordPlural: 'Entities',
    repositoryLabel: 'Schema',
    hierarchyLabel: 'References',
    importAction: 'Import Schema',
    keyColumnHint: 'Table Name',
    commitLabel: 'Push Schema',
    pullLabel: 'Refresh Schema',
    diagramLabel: 'ER diagram',
  },

  fieldAliases: {
    name: ['table', 'tableName', 'entity', 'entityName'],
    columns: ['fields', 'attrs', 'attributes', 'cols'],
    primaryKey: ['pk', 'primary_key'],
    foreignKey: ['fk', 'foreign_key', 'references'],
  },

  requiredProps: ['name'],
  recommendedProps: ['columns'],

  primaryShapeType: 'erd_entity',

  propsToAttrsMapping: {
    // Base identity
    name: 'name',
    // Schema props
    columns: 'columns',
    notation: 'notation', // crows-foot | chen | uml
    schema: 'schema',
  },

  relationshipPackId: 'software-design-relationships',
};
